import React, { useState, useRef, useEffect } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useOnboarding } from './hooks/useOnboarding';
import styles from './WelcomeOnboardingPage.module.scss';
import logo from '../../assets/images/Logo-bodhiwell.png';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TermsConditionsContent from '../TermsConditionsPage/TermsConditionsContent';
import PrivacyPolicyContent from '../PrivacyPolicyPage/PrivacyPolicyContent';
import { useTranslation } from 'react-i18next';

import posterImg from '../../assets/images/examples/bienvenida-profesionales-poster.png';
import videoSrc from '../../assets/videos/examples/bienvenida-profesionales.mp4';

const WelcomeOnboardingPage: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (i18n.language !== 'es') {
            i18n.changeLanguage('es');
        }
    }, [i18n]);

    const {
        loading,
        error,
        user,
        password,
        setPassword,
        confirmPassword,
        setConfirmPassword,
        submitSetup,
        handleGoogleSetup,
        submitting,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        termsAccepted,
        setTermsAccepted,
        showPassword,
        setShowPassword,
        showConfirmPassword,
        setShowConfirmPassword,
        email,
        setEmail
    } = useOnboarding();

    const [showVideo, setShowVideo] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);

    const [legalOpen, setLegalOpen] = useState(false);
    const [legalOrder, setLegalOrder] = useState<'terms' | 'privacy'>('terms');

    useEffect(() => {
        if (!loading && !error && user) {
            setShowVideo(true);
        }
    }, [loading, error, user]);

    useEffect(() => {
        if (showVideo && videoRef.current) {
            videoRef.current.play().catch(() => setIsPlaying(false));
        } else if (!showVideo && videoRef.current) {
            videoRef.current.pause();
        }
    }, [showVideo]);

    const handlePlayPause = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
    };

    if (loading) {
        return <div className={`${styles['page']} ${styles['welcome-page-container']}`}><div>{t('common.loading', 'Cargando...')}</div></div>;
    }

    if (error) {
        return <div className={`${styles['page']} ${styles['welcome-page-container']}`}><div>{error}</div></div>;
    }

    return (
        <div className={`${styles['page']} ${styles['welcome-page-container']}`}>
            <section className={styles['example-shell']}>
                {showVideo && (
                    <div className={styles['video-modal']}>
                        <div className={styles['video-card']}>
                            <div className={styles['video-top']}>
                                <button className={styles['close-video']} onClick={() => setShowVideo(false)}>×</button>
                            </div>
                            <div className={styles['video-wrap']}>
                                <video
                                    ref={videoRef}
                                    playsInline
                                    preload="auto"
                                    poster={posterImg}
                                    src={videoSrc}
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                    onTimeUpdate={handleTimeUpdate}
                                    onEnded={() => setShowVideo(false)}
                                    muted={isMuted}
                                />
                                {!isPlaying && (
                                    <div className={styles['video-overlay']} onClick={handlePlayPause}>
                                        <button className={styles['play-btn']}></button>
                                    </div>
                                )}
                                <div className={styles['video-controls']}>
                                    <button
                                        type="button"
                                        className={`${styles['ctrl-btn']} ${isPlaying ? styles['playing'] : styles['paused']}`}
                                        onClick={handlePlayPause}
                                    ></button>
                                    <input
                                        className={styles['progress-range']}
                                        type="range"
                                        min="0" max="100"
                                        value={progress || 0}
                                        onChange={(e) => {
                                            if (videoRef.current) {
                                                videoRef.current.currentTime = (parseFloat(e.target.value) / 100) * videoRef.current.duration;
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className={`${styles['ctrl-btn']} ${isMuted ? styles['volume-off'] : styles['volume-on']}`}
                                        onClick={() => setIsMuted(!isMuted)}
                                    ></button>
                                    <input
                                        className={styles['volume-range']}
                                        type="range"
                                        min="0" max="1" step="0.05"
                                        value={isMuted ? 0 : volume}
                                        onChange={(e) => {
                                            const v = parseFloat(e.target.value);
                                            setVolume(v);
                                            setIsMuted(v === 0);
                                            if (videoRef.current) {
                                                videoRef.current.volume = v;
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <p className={styles['video-note']}>{t('onboarding.welcomePage.videoNote', 'Mensaje de bienvenida.')}<span className={styles['audio-tip']}>{t('onboarding.welcomePage.audioTip', 'Si no inicia automáticamente, presioná play.')}</span></p>
                        </div>
                    </div>
                )}

                <div className={styles['example-grid']}>
                    <div className={styles['welcome-pane']}>
                        <div className={styles['welcome-copy']}>
                            <img className={styles['welcome-logo']} src={logo} alt="Bodhiwell" />
                            <p className={styles['eyebrow']}>{t('onboarding.welcomePage.eyebrow', 'Acceso inicial exclusivo')}</p>
                            <h2>{t('onboarding.welcomePage.hello', 'Hola {{name}}, te damos la bienvenida.', { name: user?.first_name || t('onboarding.welcomePage.defaultName', 'Profesional') })}</h2>
                            <p>{t('onboarding.welcomePage.intro1', 'Gracias por ser parte de esta primera etapa de Bodhiwell.')}</p>
                            <p>{t('onboarding.welcomePage.intro2', 'Desde aquí vas a poder crear tu usuario y comenzar a construir tu perfil dentro de la plataforma.')}</p>
                            <div className={styles['steps']}>
                                <div className={styles['step']}><span className={styles['n']}>1</span><span>{t('onboarding.welcomePage.step1', 'Completá tus datos de acceso.')}</span></div>
                                <div className={styles['step']}><span className={styles['n']}>2</span><span>{t('onboarding.welcomePage.step2', 'Confirmá tu registro.')}</span></div>
                                <div className={styles['step']}><span className={styles['n']}>3</span><span>{t('onboarding.welcomePage.step3', 'Empezá a cargar y editar tu perfil.')}</span></div>
                            </div>
                            <button className={styles['replay-link']} onClick={() => setShowVideo(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{t('onboarding.welcomePage.replayVideo', 'Volver a ver la bienvenida')}</button>
                        </div>
                    </div>

                    <aside className={styles['form-card']}>
                        <div className={styles['tabs']}>
                            <div className={styles['tab']}>{t('onboarding.welcomePage.loginTab', 'Iniciar sesión')}</div>
                            <div className={`${styles['tab']} ${styles['active']}`}>{t('onboarding.welcomePage.createAccountTab', 'Crear tu cuenta')}</div>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); submitSetup(); }}>
                            <div className={styles['fields']}>
                                {user?.first_name ? (
                                    <div className={`${styles['field']} ${styles['active']}`}>{user?.first_name}</div>
                                ) : (
                                    <input type="text" className={styles['field-input']} placeholder={t('common.firstName', 'Nombre')} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                                )}
                                {user?.last_name ? (
                                    <div className={`${styles['field']} ${styles['active']}`}>{user?.last_name}</div>
                                ) : (
                                    <input type="text" className={styles['field-input']} placeholder={t('common.lastName', 'Apellido')} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                                )}
                                {user?.email ? (
                                    <div className={`${styles['field']} ${styles['active']}`}>{user?.email}</div>
                                ) : (
                                    <input type="email" className={styles['field-input']} placeholder={t('common.email', 'Correo electrónico')} value={email} onChange={(e) => setEmail(e.target.value)} required />
                                )}
                                <div style={{ position: 'relative', width: '100%', marginBottom: '16px' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className={styles['field-input']}
                                        placeholder={t('onboarding.welcomePage.createPassword', 'Crea una contraseña')}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={8}
                                        style={{ width: '100%', margin: 0 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', color: 'rgba(19,35,29,0.5)' }}
                                    >
                                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </button>
                                </div>
                                <div style={{ position: 'relative', width: '100%', marginBottom: '16px' }}>
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        className={styles['field-input']}
                                        placeholder={t('onboarding.welcomePage.confirmPassword', 'Confirmá tu contraseña')}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        minLength={8}
                                        style={{ width: '100%', margin: 0 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', color: 'rgba(19,35,29,0.5)' }}
                                    >
                                        {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </button>
                                </div>
                                <div className={styles['switch-field']}><span className={`${styles['switch']} ${styles['switch-on']}`}></span> {t('onboarding.welcomePage.registerAsProfessional', 'Registrarme como profesional')}</div>
                            </div>
                            <div className={styles['checks']}>
                                <label className={styles['check']} style={{ cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); setTermsAccepted(!termsAccepted); }}>
                                    <span className={`${styles['box']} ${termsAccepted ? styles['box-checked'] : ''}`}></span>
                                    <span>{t('onboarding.welcomePage.readAndAccept', 'He leído y acepto los ')}<a href="#terms" onClick={e => { e.preventDefault(); e.stopPropagation(); setLegalOrder('terms'); setLegalOpen(true); }}>{t('onboarding.welcomePage.terms', 'Términos y condiciones')}</a>{t('onboarding.welcomePage.andThe', ' y la ')}<a href="#privacy" onClick={e => { e.preventDefault(); e.stopPropagation(); setLegalOrder('privacy'); setLegalOpen(true); }}>{t('onboarding.welcomePage.privacy', 'Política de privacidad')}</a>.</span>
                                </label>
                            </div>
                            <button type="submit" className={styles['primary']} style={{ width: '100%', border: 'none', cursor: 'pointer' }} disabled={submitting}>
                                {submitting ? t('onboarding.welcomePage.creatingAccount', 'Creando cuenta...') : t('onboarding.welcomePage.createAccountTab', 'Crear tu cuenta')}
                            </button>
                            <div className={styles['divider']}>{t('onboarding.welcomePage.orContinueWith', 'o continuar con')}</div>
                            <button type="button" onClick={handleGoogleSetup} className={styles['google']} style={{ width: '100%', border: '1px solid rgba(16,25,21,.12)', cursor: 'pointer' }} disabled={submitting}>
                                <span className={styles['gmark']}>G</span> {t('common.google', 'Google')}
                            </button>
                            <a className={styles['go-home']} href="/">{t('common.goHome', 'Ir al inicio')}</a>
                            <p className={styles['legal-note']}>{t('onboarding.welcomePage.legalNotePrefix', 'Al continuar, aceptas nuestros')}<br /><a href="#terms" onClick={(e) => { e.preventDefault(); setLegalOrder('terms'); setLegalOpen(true); }}>{t('onboarding.welcomePage.termsOfService', 'Términos de servicio')}</a>{t('onboarding.welcomePage.and', ' y ')}<a href="#privacy" onClick={(e) => { e.preventDefault(); setLegalOrder('privacy'); setLegalOpen(true); }}>{t('onboarding.welcomePage.privacy', 'Política de privacidad')}</a></p>
                        </form>
                    </aside>
                </div>
            </section>

            <Dialog open={legalOpen} onClose={() => setLegalOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ style: { background: 'var(--bg)' } }}>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'flex-end', pb: 0 }}>
                    <IconButton onClick={() => setLegalOpen(false)} sx={{ color: (theme) => theme.palette.grey[500] }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {legalOrder === 'terms' ? (
                        <>
                            <TermsConditionsContent />
                            <hr style={{ margin: '40px 0', border: 'none', borderTop: '1px solid rgba(16,25,21,.12)' }} />
                            <PrivacyPolicyContent />
                        </>
                    ) : (
                        <>
                            <PrivacyPolicyContent />
                            <hr style={{ margin: '40px 0', border: 'none', borderTop: '1px solid rgba(16,25,21,.12)' }} />
                            <TermsConditionsContent />
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WelcomeOnboardingPage;
